import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import compose from 'lodash.flowright';
import qs from 'query-string';

import useAuthenticated from '../../../hooks/useAuthenticated';
import { EVENT_ADDON_ITEM_TYPE } from '../../../constants/event-items';
import { AlbumFolderForSpecialtyAlbumInvites } from '../../../graphql/queries/album-folder';
import FullScreenLoader from '../../../components/global/FullScreenLoader';
import useSpecialtyEventUpload from './hooks/useSpecialtyEventUpload';
import useOpenAlert from '../../../hooks/useOpenAlert';
import {
  canViewSpecialtyEventAlbums,
  disableSpecialtyEventUploadButton,
  getSpecialtyAlbums,
} from './helpers';
import { photoUploadFilter } from '../../../util/photo-upload';
import { isVideo } from '../../../apps/pro/components/UploadProgressTracking/helpers';
import { SPECIALTY_ALBUM_GREETING_MODAL } from '../../../apps/pro/views/AlbumFolder/FreemiumEvents/SpecialtyAlbumGreetingModal';
import useOpenModal from '../../../hooks/useOpenModal';
import {
  isMemoryBook,
  isSpecialtyAlbum,
  isVideoAlbum,
  isWeddingsContent,
} from '../../../util/album';
import { STEPS } from './constants';
import HomeStep from './components/HomeStep';
import UploadingStep from './components/UploadingStep';
import VideoUploadStep from './components/VideoUploadStep';
import StandaloneStep from './components/StandaloneStep';
import PhotoUploadStep from './components/PhotoUploadStep';
import UploadSuccessStep from './components/UploadSuccessStep';
import SpecialtyEventUploadLayout from './components/SpecialtyEventUploadLayout';
import AlbumSelectStep from './components/AlbumSelectStep';
import GiftingSuccessStep from './components/GiftingSuccessStep';
import GiftingStep from './components/GiftingStep';
import withGoogleRecaptchaProvider from '../../../components/auth/hocs/withGoogleRecaptchaProvider';

const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  loading: PropTypes.bool,
  refetchAlbumFolder: PropTypes.func,
};

const SpecialtyEventUploadView = ({
  history,
  location,
  albumFolder,
  loading,
  refetchAlbumFolder,
}) => {
  const openModal = useOpenModal();
  const standaloneInputRef = useRef();
  const [targetAlbum, setTargetAlbum] = useState(null);
  const [step, setStep] = useState(STEPS.LOADING);
  const { target: targetAlbumId } = qs.parse(location.search);

  const authenticated = useAuthenticated();
  const openAlert = useOpenAlert();

  const {
    total,
    current,
    success,
    error,
    uploading,
    albumUploadReset,
    onHierarchyViewsCreate,
    onUpdateUploadedStates,
    onUpload,
    uploadedTargets,
    user,
  } = useSpecialtyEventUpload({ authenticated });

  const [
    waldofiedAlbum,
    memoryBookAlbum,
    groupVideoAlbum,
  ] = getSpecialtyAlbums(
    albumFolder?.children?.albums?.records,
    albumFolder?.album_subtype,
    [
      EVENT_ADDON_ITEM_TYPE.WALDOFIED,
      EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
      EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
    ],
  );

  const isWaldofiedDisabled = disableSpecialtyEventUploadButton(
    waldofiedAlbum,
    albumFolder?.event_settings,
    true,
  );

  // We will not use the standalone view for Waldofied Albums, only Memory Book & Video Albums
  const standaloneTarget = memoryBookAlbum ?? groupVideoAlbum;
  const queryTarget = albumFolder?.children?.albums?.records?.find(
    album => !!targetAlbumId && album.id === targetAlbumId,
  );

  const isStandaloneTarget =
    albumFolder?.individual_albums && !!standaloneTarget;

  const canContinueUploading =
    !!memoryBookAlbum ||
    !!groupVideoAlbum ||
    (!!waldofiedAlbum && !isWaldofiedDisabled);

  useEffect(() => {
    albumUploadReset();
  }, []);

  useEffect(() => {
    if (albumFolder && queryTarget) {
      setTargetAlbum(queryTarget);
      if (isVideoAlbum(queryTarget)) {
        setStep(STEPS.VIDEO_UPLOAD);
      } else {
        setStep(STEPS.PHOTO_UPLOAD);
      }
    } else if (albumFolder && isStandaloneTarget) {
      setStep(STEPS.STANDALONE_HOME);
    } else if (albumFolder && [STEPS.LOADING].includes(step)) {
      setStep(STEPS.HOME);
    }
  }, [albumFolder]);

  useEffect(() => {
    // Join the user to all the albums in the gallery that they are able to view.
    if (authenticated && albumFolder) {
      onHierarchyViewsCreate([
        albumFolder.id,
        ...albumFolder.children.albums.records
          .filter(album =>
            canViewSpecialtyEventAlbums([album], albumFolder.event_settings),
          )
          .map(album => album.id),
      ]);
    }
  }, [authenticated, albumFolder]);

  useEffect(() => {
    if (uploading) {
      setStep(STEPS.UPLOADING);
    }
  }, [uploading]);

  useEffect(() => {
    if (success && targetAlbum) {
      onUpdateUploadedStates(targetAlbum);
      setTargetAlbum(null);
    }

    // We have uploaded something
    if (success && step === STEPS.UPLOADING) {
      if (queryTarget) {
        setStep(STEPS.TARGET_UPLOAD_SUCCESS);
      } else if (isStandaloneTarget) {
        setStep(STEPS.STANDALONE_HOME);
      } else {
        setStep(STEPS.HOME);
      }
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      albumUploadReset();
      openAlert({
        type: 'error',
        title: 'Something went wrong',
        message: error,
      });
    }
  }, [error]);

  const onFilesUpload = async (uploadedFiles, album, isVideosOnly = false) => {
    const isVideoUploadOnly = isVideoAlbum(album) || isVideosOnly;
    const validFiles = isVideoUploadOnly
      ? uploadedFiles.filter(file => isVideo(file))
      : uploadedFiles.filter(
          file =>
            photoUploadFilter(file) || (!isMemoryBook(album) && isVideo(file)),
        );

    if (!validFiles.length) {
      openAlert({
        type: 'error',
        title: 'Something went wrong',
        message: 'The files chosen are not supported for this album',
      });
      return; // Do nothing.
    }

    albumUploadReset();
    setTargetAlbum(album);
    await onUpload(validFiles, album);
  };

  if (loading || !albumFolder) {
    return <FullScreenLoader />;
  }

  const renderStep = () => {
    switch (step) {
      case STEPS.GIFTING_SUCCESS:
        return (
          <GiftingSuccessStep
            setStep={setStep}
            refetchAlbumFolder={refetchAlbumFolder}
          />
        );
      case STEPS.GIFTING:
        return (
          <GiftingStep
            user={user}
            albumFolder={albumFolder}
            setStep={setStep}
            onBack={() => {
              setStep(STEPS.HOME);
            }}
          />
        );
      case STEPS.PHOTO_UPLOAD:
        return <PhotoUploadStep album={targetAlbum} onUpload={onFilesUpload} />;
      case STEPS.VIDEO_UPLOAD:
        return (
          <VideoUploadStep
            album={targetAlbum}
            albumFolder={albumFolder}
            authenticated={authenticated}
            onBack={
              targetAlbum === queryTarget
                ? () => history.goBack()
                : () => {
                    if (isStandaloneTarget) {
                      setStep(STEPS.STANDALONE_HOME);
                    } else {
                      setStep(STEPS.HOME);
                    }
                    setTargetAlbum(null);
                  }
            }
            onUpload={onFilesUpload}
            uploadOnly={!isSpecialtyAlbum(targetAlbum)}
          />
        );
      case STEPS.UPLOADING:
        return (
          <UploadingStep
            current={current}
            total={total}
            error={error}
            success={success}
          />
        );
      case STEPS.LOADING:
        return <FullScreenLoader />;
      case STEPS.STANDALONE_HOME:
        return (
          <StandaloneStep
            albumFolder={albumFolder}
            standaloneTarget={standaloneTarget}
            inputRef={standaloneInputRef}
            success={success}
            onAddPhotos={() => {
              if (isVideoAlbum(standaloneTarget)) {
                setTargetAlbum(standaloneTarget);
                setStep(STEPS.VIDEO_UPLOAD);
              } else {
                standaloneInputRef?.current?.click?.();
              }
            }}
            onUpload={onFilesUpload}
            authenticated={authenticated}
            albumUploadReset={albumUploadReset}
          />
        );
      case STEPS.TARGET_UPLOAD_SUCCESS:
        return (
          <UploadSuccessStep
            album={queryTarget}
            albumFolder={albumFolder}
            authenticated={authenticated}
          />
        );
      case STEPS.ALBUM_SELECT:
        return (
          <AlbumSelectStep
            albumFolder={albumFolder}
            waldofiedAlbum={waldofiedAlbum}
            onUpload={onFilesUpload}
            onBack={() => {
              setStep(STEPS.HOME);
              setTargetAlbum(null);
            }}
          />
        );
      case STEPS.HOME:
      default:
        return (
          <HomeStep
            albumFolder={albumFolder}
            authenticated={authenticated}
            uploadedTargets={uploadedTargets}
            onUpload={onFilesUpload}
            onVideoUpload={album => {
              if (album === groupVideoAlbum) {
                openModal(SPECIALTY_ALBUM_GREETING_MODAL, {
                  albumId: groupVideoAlbum.id,
                  headerless: true,
                  onAddPhotos: () => {
                    setTargetAlbum(groupVideoAlbum);
                    setStep(STEPS.VIDEO_UPLOAD);
                  },
                });
              } else {
                setTargetAlbum(album);
                setStep(STEPS.VIDEO_UPLOAD);
              }
            }}
            canContinueUploading={canContinueUploading}
            success={success}
            setStep={setStep}
          />
        );
    }
  };

  const isWeddings = isWeddingsContent(albumFolder);

  return (
    <SpecialtyEventUploadLayout isWeddings={isWeddings}>
      {renderStep()}
    </SpecialtyEventUploadLayout>
  );
};

SpecialtyEventUploadView.propTypes = propTypes;

const withAlbumFolderForSpecialtyAlbumInvites = graphql(
  AlbumFolderForSpecialtyAlbumInvites,
  {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumFolderId,
      },
      fetchPolicy: 'cache-and-network',
    }),
    props: ({ data: { albumFolder, loading, refetch } }) => ({
      loading,
      albumFolder,
      refetchAlbumFolder: refetch,
    }),
  },
);

export default compose(
  withRouter,
  withGoogleRecaptchaProvider,
  withAlbumFolderForSpecialtyAlbumInvites,
)(SpecialtyEventUploadView);
